import * as React from "react";
import { cn } from "@/lib/utils";
import { CustomerHeader } from "./CustomerHeader";
import { CustomerStatStrip } from "./CustomerStatStrip";
import { OverviewTab } from "./tabs/OverviewTab";
import { PetsTab } from "./tabs/PetsTab";
import { TimelineTab } from "./tabs/TimelineTab";
import { NotesTab } from "./tabs/NotesTab";
import type { CustomerDetailProps, CustomerDetailTab } from "./types";

const TAB_ORDER: CustomerDetailTab[] = ["overview", "pets", "timeline", "notes"];

export function CustomerDetail(props: CustomerDetailProps) {
  const {
    profile,
    labels,
    activeTab: controlledTab,
    onTabChange,
    onNewAppointment,
    onEditPet,
    onCallCustomer,
    onWhatsapp,
    onEmailCustomer,
    onSaveNotes,
    onBack,
    isSavingNotes,
    aiInsight,
    className,
    dataTestId,
  } = props;

  const [internalTab, setInternalTab] = React.useState<CustomerDetailTab>("overview");
  const [scrollToPetId, setScrollToPetId] = React.useState<string | undefined>();
  const activeTab = controlledTab ?? internalTab;

  const setTab = (t: CustomerDetailTab) => {
    if (controlledTab === undefined) setInternalTab(t);
    onTabChange?.(t);
  };

  const handleJumpToPet = (petId: string) => {
    setScrollToPetId(petId);
    setTab("pets");
  };

  const handleTabsKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const idx = TAB_ORDER.indexOf(activeTab);
    const nextIdx = e.key === "ArrowRight" ? (idx + 1) % TAB_ORDER.length : (idx - 1 + TAB_ORDER.length) % TAB_ORDER.length;
    setTab(TAB_ORDER[nextIdx]);
    e.preventDefault();
  };

  return (
    <div
      data-testid={dataTestId}
      className={cn("h-full flex flex-col bg-biz-bg text-biz-foreground", className)}
    >
      <CustomerHeader
        profile={profile}
        labels={labels}
        onBack={onBack}
        onCall={() => onCallCustomer?.(profile.customer.id)}
        onWhatsapp={() => onWhatsapp?.(profile.customer.id)}
        onEmail={() => onEmailCustomer?.(profile.customer.id)}
        onNewAppointment={() => onNewAppointment?.(profile.customer.id)}
      />

      <CustomerStatStrip stats={profile.stats} labels={labels} />

      {/* Tabs */}
      <div className="px-6 pt-4 border-b border-biz-border bg-biz-bg sticky top-[140px] z-10">
        <div
          role="tablist"
          aria-label={labels.tabs.overview}
          className="flex items-end gap-1"
          onKeyDown={handleTabsKeyDown}
        >
          {TAB_ORDER.map((t) => {
            const isActive = activeTab === t;
            return (
              <button
                key={t}
                role="tab"
                type="button"
                aria-selected={isActive}
                aria-controls={`customer-tab-panel-${t}`}
                id={`customer-tab-${t}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setTab(t)}
                className={cn(
                  "h-14 px-5 text-sm font-semibold biz-focus-ring transition-colors relative",
                  "border-b-2 -mb-px",
                  isActive
                    ? "text-biz-primary border-biz-primary"
                    : "text-biz-foreground-muted border-transparent hover:text-biz-foreground hover:bg-biz-muted/40",
                )}
              >
                {labels.tabs[t]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab panels */}
      <div className="flex-1 min-h-0 overflow-auto biz-scrollbar">
        <div className="px-6 py-6 max-w-[1400px] mx-auto w-full">
          <TabPanel id="overview" active={activeTab}>
            <OverviewTab
              profile={profile}
              labels={labels}
              aiInsight={aiInsight}
              onJumpToPet={handleJumpToPet}
            />
          </TabPanel>
          <TabPanel id="pets" active={activeTab}>
            <PetsTab
              pets={profile.pets}
              labels={labels}
              scrollToPetId={activeTab === "pets" ? scrollToPetId : undefined}
              onEditPet={onEditPet}
              onNewAppointment={(petId) => onNewAppointment?.(profile.customer.id, petId)}
              onAddPet={() => onEditPet?.("__new__")}
            />
          </TabPanel>
          <TabPanel id="timeline" active={activeTab}>
            <TimelineTab events={profile.timeline} labels={labels} />
          </TabPanel>
          <TabPanel id="notes" active={activeTab}>
            <NotesTab
              customerId={profile.customer.id}
              initialNotes={profile.customer.internalNotes ?? ""}
              labels={labels}
              onSave={onSaveNotes}
              isSaving={isSavingNotes}
            />
          </TabPanel>
        </div>
      </div>
    </div>
  );
}

function TabPanel({
  id,
  active,
  children,
}: {
  id: CustomerDetailTab;
  active: CustomerDetailTab;
  children: React.ReactNode;
}) {
  if (id !== active) return null;
  return (
    <div
      role="tabpanel"
      id={`customer-tab-panel-${id}`}
      aria-labelledby={`customer-tab-${id}`}
      className="motion-safe:animate-biz-fade-in"
    >
      {children}
    </div>
  );
}
